import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { z as zod } from "zod";
import { DialogWrapper } from "../../pages/SharedPages/OrganizationPage/ProductsList/BulkActionOnProducts/utils";
import {
  ConfirmOrCancelButtonContainer,
  IconButtonMedium,
  LinkButtonSmallV2,
  TertiaryButtonSmallV2,
} from "../Buttons/Buttons";
import { GenericDialogBody } from "../ConfirmDialog/ConfirmDialog";
import {
  BookmarkIcon,
  BookmarkPlusIcon,
  CaretDownIcon,
  CheckIcon,
  DeleteIcon,
  EditIcon,
} from "../Icons/Icons";
import { Row } from "../Layout/Layout";
import { TextField } from "../TextFields/TextFields";
import { TextXlBold } from "../Typography/TypographyV2";
import { zodRequiredString } from "../../util/zod.util";
import { useFormWrapper } from "../../util/util";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import type { IPresetDataItem, IPresetFilter } from "../../types/types";
import { spacingTokens } from "../../designTokens";

interface DropDownContainerTypes {
  dropdownWidth?: string;
}

const DropDownContainer = styled.div<DropDownContainerTypes>`
  box-shadow: 0px 6px 16px -6px ${({ theme }) => theme.shadowColor};
  border: solid ${({ theme }) => theme.resolve("border_md")}
    ${({ theme }) => theme.secondaryBorder};
  border-radius: 5px;
  position: absolute;
  top: 35px;
  z-index: 3;
  width: ${({ dropdownWidth }) => dropdownWidth || "400px"};
  overflow: auto;
  max-height: 224px;
  button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${({ theme }) => theme.primaryBG};
    border: none;
    width: 100%;
    white-space: nowrap;
    padding: ${({ theme }) => theme.resolve("spacing_md")}
      ${({ theme }) => theme.resolve("spacing_xs")}
      ${({ theme }) => theme.resolve("spacing_md")}
      ${({ theme }) => theme.resolve("spacing_lg")};
    cursor: pointer;
    font-size: ${({ theme }) => theme.fontSizes.small};
    font-weight: ${({ theme }) => theme.fontSizes.small};
    color: ${({ theme }) => theme.primaryTextColor};
    /* 40px (IconButtonMedium) + spacing_md padding top/bottom, fixed so the
       row's height doesn't jump between its hover/non-hover states. */
    height: 56px;
  }

  button:focus {
    background-color: ${({ theme }) => theme.secondaryButtonBG};
    outline: none;
    color: ${({ theme }) => theme.primaryTextColor};
  }

  button:hover {
    background-color: ${({ theme }) => theme.secondaryButtonBG};
    color: ${({ theme }) => theme.primaryTextColor};
  }

  .icon-container {
    display: none;
  }

  .icon-checked {
    display: flex;
    margin-right: 10px;
  }

  button:hover .icon-container {
    display: flex;
    align-items: center;
  }

  button:hover .icon-checked {
    display: none;
  }

  button > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-grow: 1;
    padding-right: 10px;
    text-align: left;
  }
`;

/**
 * `StyledIconButtonMedium` (Buttons.tsx) forces `fill`/`stroke` on every
 * direct `<svg>` child, across its base/hover/focus/disabled states — fine
 * for monochrome icons with no color of their own, but `DeleteIcon`/
 * `EditIcon` are outline-only (`fill="none"` on the `<svg>` root, color set
 * via `stroke` on each `<path>`), so that forced `fill` paints their
 * outlines in solid instead of leaving them as outlines. Reset it here
 * rather than in Buttons.tsx — other `IconButtonMedium` usages may depend
 * on the forced color for icons that don't set their own. The doubled `&&`
 * outranks `StyledIconButtonMedium`'s own (lower-specificity) rules
 * regardless of stylesheet injection order.
 */
const PresetDropdownIconButton = styled(IconButtonMedium)`
  && svg,
  &&:hover svg,
  &&:focus svg,
  &&:disabled svg {
    fill: none;
  }
`;

export const PresetFilter = ({
  dropdownWidth,
  presetFilterData,
  isUpdatePreset,
  selectedPreset,
  showPresetDialog,
  setShowPresetDialog,
  setIsEditMode,
  onDeletePreset,
  onApplyPreset,
  onUpdatePreset,
  onSubmitPreset,
}: {
  dropdownWidth?: string;
  presetFilterData: IPresetFilter | undefined;
  isUpdatePreset: boolean;
  selectedPreset: IPresetDataItem | undefined;
  showPresetDialog: boolean;
  setShowPresetDialog: Dispatch<SetStateAction<boolean>>;
  setIsEditMode: Dispatch<SetStateAction<boolean>>;
  onDeletePreset: (id: string) => Promise<void>;
  onApplyPreset: (
    id: string,
    setShowDropdown: Dispatch<SetStateAction<boolean>>
  ) => Promise<void>;
  onUpdatePreset: () => Promise<void>;
  onSubmitPreset: (preset_name: string) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const displayName = presetFilterData?.data?.[0]?.display_name || "";
  const hasPresetClear =
    !!presetFilterData?.data?.length || selectedPreset?.selected;

  // Note: `displayName` is sourced from `presetFilterData`, same as
  // `hasPresetClear`'s first condition — so once a preset exists this is
  // always `displayName`; `t("Preset")` only surfaces when a preset was
  // cleared but its selection lingers (`selectedPreset?.selected`).
  const toggleButtonLabel = selectedPreset
    ? displayName
    : hasPresetClear
    ? t("Preset")
    : displayName || t("Save Preset");

  const validationSchema = zod.object({
    preset_name: zodRequiredString(t),
  });
  type FormInputs = zod.infer<typeof validationSchema>;

  const { handleSubmit, register, reset, formState, errors } =
    useFormWrapper<FormInputs>({
      defaultValues: {
        preset_name: displayName,
      },
      resolver: zodResolver(validationSchema),
    });

  useEffect(() => {
    reset({ preset_name: displayName });
  }, [displayName, reset]);

  const toggleDialog = useCallback(
    () => setShowPresetDialog((prev) => !prev),
    [setShowPresetDialog]
  );
  const toggleDropdown = useCallback(
    () => setShowDropdown((prev) => !prev),
    []
  );

  const handleEdit = useCallback(() => {
    setIsEditMode(true);
    setShowPresetDialog(true);
  }, [setIsEditMode, setShowPresetDialog]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <div style={{ display: "flex", gap: spacingTokens.spacing_sm }}>
        <TertiaryButtonSmallV2
          testid="preset-filter-toggle-button"
          onClick={displayName ? toggleDropdown : toggleDialog}
        >
          <Row style={{ gap: spacingTokens.spacing_sm }}>
            {displayName ? (
              <BookmarkIcon width={16} height={16} />
            ) : (
              <BookmarkPlusIcon width={16} height={16} />
            )}
            <span>{toggleButtonLabel}</span>
            {displayName && <CaretDownIcon width={16} height={16} />}
          </Row>
        </TertiaryButtonSmallV2>
        {!isUpdatePreset && !!selectedPreset && (
          <LinkButtonSmallV2
            testid="update-preset-button"
            onClick={onUpdatePreset}
          >
            {t("Update Preset")}
          </LinkButtonSmallV2>
        )}
      </div>
      <GenericDialogBody
        show={showPresetDialog}
        closeDialog={() => setShowPresetDialog(false)}
      >
        <DialogWrapper style={{ gap: spacingTokens.spacing_3xl }}>
          <TextXlBold as="h3" style={{ margin: 0 }}>
            {t("Save Preset")}
          </TextXlBold>
          <TextField
            name="preset_name"
            label={t("Preset Name")}
            theref={register({
              required: true,
            })}
            formState={formState}
            errors={errors}
            type="text"
          />
          <ConfirmOrCancelButtonContainer
            confirmText={t("Save")}
            isConfirmLoading={false}
            onConfirm={handleSubmit((data) => onSubmitPreset(data.preset_name))}
            onCancel={toggleDialog}
          />
        </DialogWrapper>
      </GenericDialogBody>
      {showDropdown && (
        <DropDownContainer ref={dropdownRef} dropdownWidth={dropdownWidth}>
          {presetFilterData?.data?.map((item) => {
            return (
              <button
                key={item.id}
                data-testid={`preset-filter-item-${item.id}`}
                onClick={() => onApplyPreset(item.id, setShowDropdown)}
              >
                <span>{item.display_name}</span>
                <div className="icon-container">
                  <PresetDropdownIconButton
                    testid={`delete-preset-button-${item.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeletePreset(item.id);
                    }}
                  >
                    <DeleteIcon width={20} height={20} />
                  </PresetDropdownIconButton>
                  <PresetDropdownIconButton
                    testid={`edit-preset-button-${item.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit();
                    }}
                  >
                    <EditIcon width={20} height={20} />
                  </PresetDropdownIconButton>
                </div>
                {!!selectedPreset && (
                  <div className="icon-checked">
                    <CheckIcon fill={theme.primaryButtonBorder} />
                  </div>
                )}
              </button>
            );
          })}
        </DropDownContainer>
      )}
    </>
  );
};
